<script lang="ts">
  import QuickSearch from '../common/QuickSearch.svelte';
  import { addStudent, quickSearchCurrentClasses, updateClinicalGroup } from '$lib/api/classes';
  import type { UserClassSummary } from '$lib/api/types';
  import ClinicalGroupQuickSearch from '../common/ClinicalGroupQuickSearch.svelte';
  import Label from '../common/bootstrap/Label.svelte';
  import FormModal from '../common/modal/FormModal.svelte';
  import TableMessage from '../TableMessage.svelte';
  import { toast } from '$lib/stores/toast.svelte';
  import { utcNowIso } from '$lib/util';

  interface Props {
    classesPromise?: Promise<UserClassSummary[]>;
    filterClinicalGroups: boolean;
    header: string;
    onclose: () => void;
    userId: number;
    userRole: string;
  }

  let { classesPromise, filterClinicalGroups, header, onclose, userId, userRole }: Props = $props();

  let today = utcNowIso().split('T')[0];
  let currentUpcomingClassPromise = classesPromise?.then(classes => {
    return classes?.filter(c => c.endDate >= today);
  });

  let classIdValue: string | null = $state(null);
  let clinicalGroupIdValue: string | null = $state(null);

  let classTouched = $state(false);
  let clinicalGroupsTouched = $state(false);
</script>

<FormModal
  scrollable={false}
  formId="add_to_class"
  {header}
  {onclose}
  onsubmit={async () => {
    const classId = parseInt(classIdValue!);
    const clinicalGroupId = parseInt(clinicalGroupIdValue!);

    if (userRole === 'student') {
      await addStudent(classId, userId, clinicalGroupId);
    } else {
      await updateClinicalGroup(classId, clinicalGroupId, { instructorId: userId });
    }

    toast.showToast('success', 'User successfully added to class!');
    return true;
  }}
  validationChecks={() => {
    classTouched = true;
    clinicalGroupsTouched = true;
    return classIdValue !== null && clinicalGroupIdValue != null;
  }}
  invalidateUrl={url => url.pathname.startsWith('/api/users')}
>
  {#await currentUpcomingClassPromise}
    <TableMessage message="Loading user classes..." />
  {:then currentUpcomingClass}
    <div class="mb-3">
      <Label for="class_quicksearch" class="is-required">Class</Label>
      <QuickSearch
        bind:value={classIdValue}
        id="class_quicksearch"
        placeholder="Choose a class"
        bind:touched={classTouched}
        invalid={classTouched && classIdValue === null}
        feedback="Please choose a class."
        loadData={async (query, page, pageSize, signal) => {
          let classes = await quickSearchCurrentClasses(query, page, pageSize, signal);
          if (currentUpcomingClass) {
            classes = classes.filter(x => !currentUpcomingClass.some(c => c.classId === x.id));
          }
          return classes.map(x => {
            return {
              value: x.id.toString(),
              text: x.name,
              title: x.name,
              subtitle: x.course.code,
            };
          });
        }}
        emptySearchResultsLabel="No classes found!"
      />
    </div>
    <div>
      <Label for="clinical_group_quicksearch" class="is-required">Clinical Group</Label>
      <ClinicalGroupQuickSearch
        required
        id="clinical_group_quicksearch"
        classId={classIdValue === null ? null : parseInt(classIdValue)}
        bind:value={clinicalGroupIdValue}
        disabled={!classIdValue}
        {filterClinicalGroups}
        bind:touched={clinicalGroupsTouched}
      />
    </div>
  {/await}
</FormModal>
